import React from 'react';
import styled from 'styled-components';
import PaymentLogo from './PaymentLogo';

const Card = styled.button`
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  width: 100%;
  padding: 1rem;
  background: ${({ $selected }) => ($selected ? '#fff6f6' : '#fff')};
  border: 1.5px solid ${({ $selected }) => ($selected ? '#ff5a5f' : '#ddd')};
  border-radius: 10px;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #ff5a5f;
  }

  &:focus-visible {
    outline: 2px solid #ff5a5f;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: #fafafa;
  }
`;

const Radio = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => ($selected ? '#ff5a5f' : '#bbb')};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $selected }) => ($selected ? '#ff5a5f' : 'transparent')};
  }
`;

const Body = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Title = styled.span`
  font-weight: 600;
  color: #222;
`;

const Badge = styled.span`
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #eafaf1;
  color: #1b7a43;
  font-size: 0.7rem;
  font-weight: 600;
`;

const Description = styled.span`
  font-size: 0.85rem;
  color: #666;
`;

const Unavailable = styled.span`
  font-size: 0.8rem;
  color: #c0392b;
`;

const Logos = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.25rem;
`;

/**
 * One selectable payment option.
 * option: { id, title, description, logos: string[], badge?, unavailableReason? }
 */
const PaymentOptionCard = ({ option, selected = false, disabled = false, onSelect }) => {
  const isDisabled = disabled || Boolean(option.unavailableReason);

  return (
    <Card
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={isDisabled}
      $selected={selected}
      onClick={() => onSelect?.(option.id)}
    >
      <Radio $selected={selected} aria-hidden="true" />
      <Body>
        <TitleRow>
          <Title>{option.title}</Title>
          {option.badge && <Badge>{option.badge}</Badge>}
        </TitleRow>
        {option.description && <Description>{option.description}</Description>}
        {option.unavailableReason && <Unavailable>{option.unavailableReason}</Unavailable>}
        {option.logos?.length > 0 && (
          <Logos>
            {option.logos.map((logoId) => (
              <PaymentLogo key={logoId} id={logoId} />
            ))}
          </Logos>
        )}
      </Body>
    </Card>
  );
};

export default PaymentOptionCard;
